import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
 DropdownMenu,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
 Building2,
 Calendar,
 MoreVertical,
 Edit,
 Trash2,
 Play,
 ExternalLink,
 Plus,
 FileText,
 PlayCircle,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useJobProfileStore } from '@/stores/jobProfileStore';
import type { JobProfile } from '@/types/jobProfile';

interface ProfileListProps {
 onCreateNew: () => void;
 onStartInterview?: (profile: JobProfile) => void;
 onEdit?: (profile: JobProfile) => void;
}

export function ProfileList({
 onCreateNew,
 onStartInterview,
 onEdit,
}: ProfileListProps) {
 const { toast } = useToast();
 const { profiles, isLoading, error, fetchProfiles, deleteProfile } =
  useJobProfileStore();

 useEffect(() => {
  fetchProfiles();
 }, [fetchProfiles]);

 const handleDelete = async (profileId: string) => {
  if (!confirm('Are you sure you want to delete this profile?')) {
   return;
  }

  try {
   await deleteProfile(profileId);
   toast({
    title: 'Profile Deleted',
    description: 'The interview profile has been removed.',
   });
  } catch (error: any) {
   toast({
    title: 'Deletion Failed',
    description: error.message || 'Could not remove profile.',
    variant: 'destructive',
   });
  }
 };

 const formatDate = (dateString: string | Date) => {
  return new Date(dateString).toLocaleDateString('en-US', {
   year: 'numeric',
   month: 'short',
   day: 'numeric',
  });
 };

 if (isLoading && profiles.length === 0) {
  return (
   <div className='space-y-4'>
    <div className='border border-border p-4 bg-background animate-pulse h-32'>
     <div className='h-4 bg-muted w-1/3 mb-4'></div>
     <div className='h-4 bg-muted w-1/2'></div>
    </div>
   </div>
  );
 }

 if (error && profiles.length === 0) {
  return (
   <Alert
    variant='destructive'
    className='rounded-none border-destructive/50 bg-destructive/10'
   >
    <AlertDescription className='font-mono'>
     Error: {error}
     <Button
      variant='outline'
      size='sm'
      onClick={fetchProfiles}
      className='ml-4 rounded-none h-6 text-xs'
     >
      Retry
     </Button>
    </AlertDescription>
   </Alert>
  );
 }

 if (profiles.length === 0) {
  return (
   <div className='border border-border border-dashed bg-background/50 p-12 flex flex-col items-center justify-center text-center'>
    <div className='w-16 h-16 bg-muted/20 flex items-center justify-center mb-6 rounded-full'>
     <FileText className='w-8 h-8 text-muted-foreground' />
    </div>
    <h3 className='text-lg font-bold font-mono mb-2'>NO INTERVIEWS</h3>
    <p className='text-muted-foreground font-mono text-sm max-w-md mb-8'>
     You haven't set up any interview profiles yet. Create one to get started.
    </p>
    <Button onClick={onCreateNew} className='rounded-none font-mono'>
     <Plus className='w-4 h-4 mr-2' />
     CREATE PROFILE
    </Button>
   </div>
  );
 }

 return (
  <div className='space-y-8'>
   <div className='flex justify-between items-center bg-background border-b border-border pb-6'>
    <div>
     <h2 className='text-2xl font-bold tracking-tight'>MY INTERVIEWS</h2>
     <p className='text-muted-foreground font-mono text-xs mt-1'>
      {profiles.length} ACTIVE {profiles.length === 1 ? 'PROFILE' : 'PROFILES'}
     </p>
    </div>
    <Button onClick={onCreateNew} className='rounded-none font-mono text-xs'>
     <Plus className='w-4 h-4 mr-2' />
     NEW PROFILE
    </Button>
   </div>

   <div className='grid gap-4'>
    {profiles.map((profile) => (
     <div
      key={profile.id}
      className='group border border-border bg-background hover:border-primary transition-all duration-200 relative'
     >
      <div className='absolute top-0 left-0 w-1 h-full bg-primary transform -translate-x-full group-hover:translate-x-0 transition-transform duration-200' />
      <div className='p-6 flex flex-col md:flex-row gap-6 justify-between items-start'>
       <div className='flex-1 space-y-4'>
        <div>
         <div className='flex items-center gap-2 mb-1'>
          <h3 className='text-xl font-bold'>{profile.jobTitle}</h3>
          <Badge
           variant='outline'
           className='rounded-none font-mono text-[10px]'
          >
           {profile.interviewType.toUpperCase()}
          </Badge>
         </div>
         <div className='flex items-center text-muted-foreground font-mono text-sm'>
          <Building2 className='w-3 h-3 mr-2' />
          {profile.companyName}
         </div>
        </div>
        <div className='flex flex-wrap gap-2'>
         {profile.skills.slice(0, 6).map((skill, index) => (
          <span
           key={index}
           className='px-1.5 py-0.5 bg-primary/5 border border-primary/20 text-primary text-[10px] font-mono'
          >
           {skill}
          </span>
         ))}
         {profile.skills.length > 6 && (
          <span className='px-1.5 py-0.5 text-muted-foreground text-[10px] font-mono'>
           +{profile.skills.length - 6}
          </span>
         )}
        </div>
       </div>
       <div className='flex flex-col items-end gap-3'>
        <div className='flex items-center gap-2'>
         {onStartInterview && (
          <Button
           size='sm'
           onClick={() => onStartInterview(profile)}
           className='rounded-none font-mono text-xs bg-primary text-primary-foreground hover:bg-primary/90'
          >
           <PlayCircle className='w-3 h-3 mr-2' />
           START
          </Button>
         )}
         <DropdownMenu>
          <DropdownMenuTrigger asChild>
           <Button
            variant='outline'
            size='icon'
            className='rounded-none h-9 w-9 border-border'
           >
            <MoreVertical className='w-4 h-4' />
           </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
           align='end'
           className='rounded-none border-border'
          >
           {onStartInterview && (
            <DropdownMenuItem
             onClick={() => onStartInterview(profile)}
             className='rounded-none font-mono text-xs'
            >
             <Play className='w-3 h-3 mr-2' />
             Start Session
            </DropdownMenuItem>
           )}
           {onEdit && (
            <DropdownMenuItem
             onClick={() => onEdit(profile)}
             className='rounded-none font-mono text-xs'
            >
             <Edit className='w-3 h-3 mr-2' />
             Edit Profile
            </DropdownMenuItem>
           )}
           {profile.jobUrl && (
            <DropdownMenuItem
             asChild
             className='rounded-none font-mono text-xs'
            >
             <a href={profile.jobUrl} target='_blank' rel='noopener noreferrer'>
              <ExternalLink className='w-3 h-3 mr-2' />
              View Job Post
             </a>
            </DropdownMenuItem>
           )}
           <DropdownMenuItem
            onClick={() => handleDelete(profile.id)}
            className='text-red-500 focus:text-red-500 rounded-none font-mono text-xs'
           >
            <Trash2 className='w-3 h-3 mr-2' />
            Delete
           </DropdownMenuItem>
          </DropdownMenuContent>
         </DropdownMenu>
        </div>
        <div className='flex items-center text-[10px] text-muted-foreground font-mono'>
         <Calendar className='w-3 h-3 mr-1' />
         CREATED: {formatDate(profile.createdAt).toUpperCase()}
        </div>
       </div>
      </div>
     </div>
    ))}
   </div>
  </div>
 );
}
